// src/features/streams/components/CreateStreamModal.tsx

import { useState, useMemo, useEffect } from 'react';
import { Modal, TextInput, Textarea, Select, Switch, Button, Stack, Group, Divider } from '@mantine/core';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { notifications } from '@mantine/notifications';
import { useCreateStream } from '../useStreamMutations';
import { StreamUtils, type CreateStreamFormData } from '../StreamUtils';
import { STREAM_CONSTRAINTS, STREAM_OUTPUT_CONSTRAINTS } from '@/lib/constants/stream';
import { StreamOutputsEditor, createDefaultOutput, type StreamOutputItem } from './StreamOutputsEditor';
import type { IBackendLiveData } from '@/lib/types/Customer';

const CreateStreamSchema = z.object({
  title: z.string()
    .min(STREAM_CONSTRAINTS.TITLE_MIN, 'Title is required')
    .max(STREAM_CONSTRAINTS.TITLE_MAX, `Title cannot exceed ${STREAM_CONSTRAINTS.TITLE_MAX} characters`),
  descShort: z.string()
    .max(STREAM_CONSTRAINTS.DESC_SHORT_MAX, `Description cannot exceed ${STREAM_CONSTRAINTS.DESC_SHORT_MAX} characters`),
  recording: z.boolean(),
  backendId: z.string().min(1, 'Server location is required'),
  dvr: z.boolean(),
  encryptHlsAes: z.boolean(),
});

type CreateStreamFormValues = z.infer<typeof CreateStreamSchema>;

interface CreateStreamModalProps {
  opened: boolean;
  onClose: () => void;
  liveBackends: IBackendLiveData[];
  allowDvr: boolean;
  allowEncryptLive: boolean;
}

export function CreateStreamModal({
  opened,
  onClose,
  liveBackends,
  allowDvr,
  allowEncryptLive,
}: CreateStreamModalProps) {
  const createMutation = useCreateStream();

  // Outputs managed as local state (dynamic array, not suited for flat Zod schema)
  const [outputs, setOutputs] = useState<StreamOutputItem[]>([createDefaultOutput()]);

  const { control, handleSubmit, reset, watch, setValue, formState: { isSubmitting } } = useForm<CreateStreamFormValues>({
    resolver: zodResolver(CreateStreamSchema),
    defaultValues: {
      title: '',
      descShort: '',
      recording: false,
      backendId: '',
      dvr: false,
      encryptHlsAes: false,
    },
  });

  const titleField = useController({ name: 'title', control });
  const descField = useController({ name: 'descShort', control });
  const recordingField = useController({ name: 'recording', control });
  const backendIdField = useController({ name: 'backendId', control });
  const dvrField = useController({ name: 'dvr', control });
  const encryptField = useController({ name: 'encryptHlsAes', control });

  const dvrValue = watch('dvr');
  const encryptValue = watch('encryptHlsAes');

  // Filter backends by exact DVR/encryption match
  const compatibleBackends = useMemo(
    () => StreamUtils.filterCompatibleBackends(liveBackends, {
      dvr: dvrValue,
      encryptHlsAes: encryptValue,
    }),
    [liveBackends, dvrValue, encryptValue],
  );

  const backendOptions = useMemo(
    () => compatibleBackends.map(b => ({
      value: String(b.backendId),
      label: b.locationLabel,
    })),
    [compatibleBackends],
  );

  // Auto-select default backend when compatible list changes
  useEffect(() => {
    const currentId = backendIdField.field.value;
    const isCurrentValid = compatibleBackends.some(b => String(b.backendId) === currentId);

    if (!isCurrentValid) {
      const defaultBackend = StreamUtils.selectDefaultBackend(liveBackends, {
        dvr: dvrValue,
        encryptHlsAes: encryptValue,
      });
      setValue('backendId', defaultBackend ? String(defaultBackend.backendId) : '');
    }
  }, [compatibleBackends, liveBackends, dvrValue, encryptValue, backendIdField.field.value, setValue]);

  // DVR/Encryption mutual exclusivity
  const handleDvrChange = (checked: boolean) => {
    dvrField.field.onChange(checked);
    if (checked) {
      setValue('encryptHlsAes', false);
    }
  };

  const handleEncryptChange = (checked: boolean) => {
    encryptField.field.onChange(checked);
    if (checked) {
      setValue('dvr', false);
    }
  };

  const validateOutputs = (): string | null => {
    if (outputs.length < STREAM_OUTPUT_CONSTRAINTS.MIN_OUTPUTS) return 'At least one stream output is required';
    for (let i = 0; i < outputs.length; i++) {
      const o = outputs[i];
      if (o.totalKbps < STREAM_OUTPUT_CONSTRAINTS.MIN_BITRATE_KBPS || o.totalKbps > STREAM_OUTPUT_CONSTRAINTS.MAX_BITRATE_KBPS) {
        return `Output ${i + 1} bitrate must be between ${STREAM_OUTPUT_CONSTRAINTS.MIN_BITRATE_KBPS} and ${STREAM_OUTPUT_CONSTRAINTS.MAX_BITRATE_KBPS} kbps`;
      }
    }
    return null;
  };

  const onSubmit = async (data: CreateStreamFormValues) => {
    const outputError = validateOutputs();
    if (outputError) {
      notifications.show({ title: 'Validation Error', message: outputError, color: 'orange' });
      return;
    }

    const formData: CreateStreamFormData = {
      title: data.title,
      descShort: data.descShort,
      recordMode: data.recording ? 'automatic' : 'manual',
      dvr: data.dvr,
      encryptHlsAes: data.encryptHlsAes,
      outputs,
    };

    const backendId = parseInt(data.backendId, 10);
    const request = StreamUtils.buildCreateRequest(formData, backendId);

    try {
      await createMutation.mutateAsync(request);
      notifications.show({
        title: 'Created',
        message: `Stream "${data.title}" created successfully.`,
        color: 'green',
      });
      handleClose();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create stream',
        color: 'red',
      });
    }
  };

  const onInvalid = (fieldErrors: Record<string, { message?: string }>) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({ title: 'Validation Error', message: firstError.message, color: 'orange' });
    }
  };

  const handleClose = () => {
    reset();
    setOutputs([createDefaultOutput()]);
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Create Stream" size="lg">
      <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <Stack gap="md">
          <TextInput
            label="Title"
            placeholder="Enter stream title"
            required
            maxLength={STREAM_CONSTRAINTS.TITLE_MAX}
            description={`${titleField.field.value.length}/${STREAM_CONSTRAINTS.TITLE_MAX}`}
            value={titleField.field.value}
            onChange={titleField.field.onChange}
            onBlur={titleField.field.onBlur}
            error={titleField.fieldState.error?.message}
          />

          <Textarea
            label="Description"
            placeholder="Optional short description"
            maxLength={STREAM_CONSTRAINTS.DESC_SHORT_MAX}
            description={`${descField.field.value.length}/${STREAM_CONSTRAINTS.DESC_SHORT_MAX}`}
            value={descField.field.value}
            onChange={descField.field.onChange}
            onBlur={descField.field.onBlur}
            error={descField.fieldState.error?.message}
            rows={3}
          />

          <Divider />

          <Select
            label="Server Location"
            description="Select the location closest to the broadcaster"
            placeholder="Select location"
            required
            data={backendOptions}
            value={backendIdField.field.value}
            onChange={(val) => val && backendIdField.field.onChange(val)}
            allowDeselect={false}
            error={backendIdField.fieldState.error?.message}
          />

          <Switch
            label="Recording"
            description={recordingField.field.value
              ? 'All broadcasts will be automatically recorded'
              : 'Recording is off — can be enabled manually per broadcast'}
            checked={recordingField.field.value}
            onChange={(e) => recordingField.field.onChange(e.currentTarget.checked)}
          />

          {allowDvr && (
            <Switch
              label="DVR"
              description={dvrValue
                ? 'Viewers can rewind and seek within the live stream'
                : 'Viewers watch live only — no rewind or seek'}
              checked={dvrField.field.value}
              onChange={(e) => handleDvrChange(e.currentTarget.checked)}
              disabled={encryptValue}
            />
          )}

          {allowEncryptLive && (
            <Switch
              label="Stream Encryption"
              description={encryptValue
                ? 'HLS output is AES-128 encrypted'
                : 'HLS output is not encrypted'}
              checked={encryptField.field.value}
              onChange={(e) => handleEncryptChange(e.currentTarget.checked)}
              disabled={dvrValue}
            />
          )}

          <Divider />

          <StreamOutputsEditor outputs={outputs} onChange={setOutputs} />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting || createMutation.isPending}>
              Create Stream
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
